import React from 'react';
import Typography from '../../../../../components/common/Typography';
import Grid from '@material-ui/core/Grid';
import useStyles from '../../../styles';
import { useTrans } from '../../../../../services/i18n';
import CodeInput from '../../../../../components/common/Inputs/CodeInput';
import CheckBox from '../../../../../components/common/Inputs/CheckBox';
import { Button } from '../../../../../components/common/Buttons';
import { IconBack, IconHelp } from '../../../../../components/svg';
import IconButton from '@material-ui/core/IconButton';
import { AUTH_SMS_MFA } from '../../../../../constants';

const Authentication = ({ user, challenge, onSubmit, onCancel, pending = false, error, onClearError }) => {
  const classes = useStyles();
  const { trans } = useTrans();
  const [code, setCode] = React.useState('');
  const [rememberDevice, setRememberDevice] = React.useState(false);

  const onChangeHandle = React.useCallback(
    (value) => {
      setCode(value);
      if (error && onClearError) {
        onClearError();
      }
    },
    [error, onClearError]
  );

  const onSubmitHandle = React.useCallback(() => {
    if (pending || code.length !== 6) {
      return;
    }

    onSubmit && onSubmit(code, rememberDevice);
  }, [code, onSubmit, pending, rememberDevice]);

  return (
    <div className={classes.formBack}>
      <Grid container spacing={0} justifyContent="center" className={classes.content}>
        <Grid item xs={12} className={classes.topHeader}>
          <IconButton edge="start" color="inherit" size="small" onClick={onCancel} disabled={pending}>
            <IconBack />
          </IconButton>
        </Grid>
        <Grid item xs={12} className={classes.signForm}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h1Medium" fontFamily="lexend" weight="500">
                {trans('login-to-USFCR')}
              </Typography>
              <Typography weight="500" className={classes.codeHelperText}>
                {trans('an-authentication-code-is-required')}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography gutter="label" variant="h6" fontFamily="lexend" weight="600" color="label">
                {trans('verification-code')}
              </Typography>
              {challenge === AUTH_SMS_MFA && (
                <Typography weight="500" color="label" variant="h6">
                  A code has been sent to the phone number registered in the application ending in...
                </Typography>
              )}
              <Typography variant="h6">{user?.challengeParam?.CODE_DELIVERY_DESTINATION}</Typography>
            </Grid>
            <Grid item xs={12} className={classes.codeRow}>
              <CodeInput type="number" onChange={onChangeHandle} disabled={pending} />
              {error && (
                <Typography color="error" className={classes.mfaErrorText}>
                  {error}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} className={classes.helpRow}>
              <IconHelp />
              <Typography color="hint" component="p" style={{ marginLeft: 10 }}>
                {trans('enter-the-code-from-your-authentication-app')}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <label htmlFor="remember-device" className={classes.rememberRow}>
                <CheckBox
                  dense
                  id="remember-device"
                  checked={rememberDevice}
                  disabled={pending}
                  onChange={(event) => setRememberDevice(event.target.checked)}
                />
                <Typography color="label" weight="500" variant="h6" className={classes.rememberLabel}>
                  {trans('remember-this-device-for-30-days')}
                </Typography>
              </label>
            </Grid>
            <Grid item xs={12} className={classes.mfaActions}>
              <Button
                id="mfa-verify-btn"
                className={classes.loginButton}
                variant="contained"
                size="large"
                color="primary"
                pending={pending}
                disabled={code.length !== 6}
                onClick={onSubmitHandle}
              >
                {trans('verify')}
              </Button>
              {pending && (
                <Typography color="label" weight="500" variant="h6" className={classes.mfaVerifyingText}>
                  {trans('verifying-authentication-code')}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} className={classes.flexHelper} />
      </Grid>
    </div>
  );
};

export default Authentication;
